// LIGHT COPY of pages/TopologyPage.jsx for /event-trace-light. The dark original is untouched;
// changes to it are NOT carried here automatically.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { fmtUtc, SITE_NATIVE_PREFIX } from '../constants/topologyLight';
import TopologyAssets from '../components/topology-light/TopologyAssetsLight';
import TopologyAttackChain from '../components/topology-light/TopologyAttackChainLight';
import TopologyProtocols from '../components/topology-light/TopologyProtocolsLight';
import TopologyOverview from '../components/topology-light/TopologyOverviewLight';
import TopologyGlobal from '../components/topology-light/TopologyGlobalLight';
import { edgeNodeDisplayName } from '../utils/edgeNodeDisplayName';
import { EVENT_SAMPLE_LIMIT, fetchEventTotal } from '../utils/counts';
import ThemeToggle from '../components/ThemeToggle';
import './EventTraceLightDark.css';

const API_BASE = process.env.REACT_APP_API_URL || '';
const POLL_INTERVAL = 60000;

// Dark theme (styling only): the page root carries data-etl-theme, which EventTraceLightDark.css reads.
// The viewer's choice is kept in localStorage; storage that is unavailable or throws falls back to light
// and is reported to the console, never to the page.
export const ETL_THEME_KEY = 'eventTraceLight.theme';

export function readStoredTheme() {
  try {
    return window.localStorage.getItem(ETL_THEME_KEY) === 'dark' ? 'dark' : 'light';
  } catch (err) {
    console.warn('Event Trace Light: theme preference unreadable, using light', err);
    return 'light';
  }
}

export function storeTheme(theme) {
  try {
    window.localStorage.setItem(ETL_THEME_KEY, theme);
  } catch (err) {
    console.warn('Event Trace Light: theme preference not saved', err);
  }
}

const MODES = [
  { id: 'overview',      label: 'Overview' },
  { id: 'assets',        label: 'Assets' },
  { id: 'protocols',     label: 'Protocols' },
  { id: 'attack-chain',  label: 'Event Trace' },
  { id: 'global',        label: 'Global' },
];

async function fetchJSON(path) {
  const res = await fetch(`${API_BASE}${path}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`${path} → ${res.status}`);
  return res.json();
}

function AgentSelector({ edgeNodes, selectedId, onSelect, theme = 'light' }) {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef(null);
  const ref = useRef(null);
  const dropRef = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      const inButton = ref.current && ref.current.contains(e.target);
      const inDrop = dropRef.current && dropRef.current.contains(e.target);
      if (!inButton && !inDrop) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleToggle() {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setDropPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
    }
    setOpen(o => !o);
  }

  const selected = edgeNodes.find(n => n.edge_node_id === selectedId);
  const isOnline = n => n.status === 'Online' || n.status === 'Active';

  const dropdown = open ? (
    <div
      ref={dropRef}
      data-etl-theme={theme}
      style={{ position: 'fixed', top: dropPos.top, right: dropPos.right, zIndex: 9999 }}
      className="etl-portal w-56 bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden"
    >
      <div className="px-3 py-2 border-b border-slate-200">
        <span className="text-[11px] text-slate-600 uppercase tracking-wider">Sites</span>
      </div>
      {[...edgeNodes]
        .sort((a, b) => (a.edge_node_id || '').localeCompare(b.edge_node_id || ''))
        .map(node => (
          <button
            key={node.edge_node_id}
            onClick={() => { onSelect(node.edge_node_id); setOpen(false); }}
            className={`w-full flex items-center gap-2 px-3 py-2.5 text-left text-xs hover:bg-slate-200/50 transition-colors ${
              selectedId === node.edge_node_id ? 'bg-cyan-100' : ''
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isOnline(node) ? 'bg-emerald-500' : 'bg-slate-300'}`} />
            <div>
              <div className={`font-medium ${selectedId === node.edge_node_id ? 'text-cyan-700' : 'text-slate-800'}`}>
                {edgeNodeDisplayName(node)}
              </div>
              <div className="text-[11px] text-slate-600 font-mono">{node.edge_node_id}</div>
            </div>
            {selectedId === node.edge_node_id && (
              <svg className="w-3 h-3 text-cyan-700 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </button>
        ))}
      {edgeNodes.length === 0 && (
        <div className="px-3 py-3 text-xs text-slate-600">No sites registered</div>
      )}
    </div>
  ) : null;

  return (
    <div ref={ref} className="relative">
      <button
        ref={btnRef}
        onClick={handleToggle}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100/60 border border-slate-200 hover:border-cyan-400 transition-colors text-xs"
      >
        {selected ? (
          <>
            <span className={`w-1.5 h-1.5 rounded-full ${isOnline(selected) ? 'bg-emerald-500' : 'bg-slate-300'}`} />
            <span className="text-slate-800">{edgeNodeDisplayName(selected)}</span>
          </>
        ) : (
          <span className="text-slate-600">Select site…</span>
        )}
        <svg className="w-3 h-3 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {createPortal(dropdown, document.body)}
    </div>
  );
}

export default function EventTraceLight() {
  const [activeMode, setActiveMode] = useState('attack-chain');
  const [assets, setAssets] = useState([]);
  const [edges, setEdges] = useState([]);
  const [events, setEvents] = useState([]);
  const [events24h, setEvents24h] = useState([]);
  const [edgeNode, setEdgeNode] = useState(null);
  const [edgeNodes, setEdgeNodes] = useState([]);
  const [selectedEdgeNodeId, setSelectedEdgeNodeId] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);
  const [windowHours, setWindowHours] = useState(2);
  const [eventTotal, setEventTotal] = useState(null); // the true total beside the capped rows (R-500)
  const pollRef = useRef(null);
  const [theme, setTheme] = useState(readStoredTheme);
  const toggleTheme = useCallback(() => {
    const next = theme === 'dark' ? 'light' : 'dark';
    storeTheme(next);
    setTheme(next);
  }, [theme]);

  // Fetch all edge nodes once to populate the selector
  useEffect(() => {
    fetchJSON('/edge-nodes/')
      .then(data => {
        const nodes = Array.isArray(data) ? data : (data.data || []);
        setEdgeNodes(nodes);
        if (nodes.length > 0 && !selectedEdgeNodeId) {
          setSelectedEdgeNodeId(nodes[0].edge_node_id);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nodeParam = selectedEdgeNodeId ? `&edge_node_id=${encodeURIComponent(selectedEdgeNodeId)}` : '';
  const nodeParamQ = selectedEdgeNodeId ? `?edge_node_id=${encodeURIComponent(selectedEdgeNodeId)}` : '';
  const nativePrefix = selectedEdgeNodeId ? (SITE_NATIVE_PREFIX[selectedEdgeNodeId] || null) : null;
  const assetParam = nodeParamQ + (nativePrefix ? `${nodeParamQ ? '&' : '?'}ip_prefix=${encodeURIComponent(nativePrefix)}` : '');

  const fetchAll = useCallback(async () => {
    try {
      // ev is capped at EVENT_SAMPLE_LIMIT rows; total is the true count over the same range (R-500).
      // fetchEventTotal never rejects: a failure is shown as "(capped; total unavailable)".
      const [a, ed, ev, en, ev24, total] = await Promise.all([
        fetchJSON(`/topology/assets${assetParam}`),
        fetchJSON(`/topology/edges?hours_back=24${nodeParam}`),
        fetchJSON(`/topology/events?hours_back=${windowHours}&limit=${EVENT_SAMPLE_LIMIT}${nodeParam}`),
        fetchJSON(`/topology/edge-node${nodeParamQ}`),
        fetchJSON(`/topology/events?hours_back=24&limit=2000${nodeParam}`),
        fetchEventTotal(({ hours_back }) => fetchJSON(`/topology/events/count?hours_back=${hours_back}${nodeParam}`),
          { siteId: selectedEdgeNodeId, hoursBack: windowHours }),
      ]);
      setAssets(a);
      setEdges(ed);
      setEvents(ev);
      setEventTotal(total);
      setEdgeNode(en);
      setEvents24h(ev24);
      setError(null);
      // Show last event timestamp (data freshness), not UI fetch time
      const latestTs = ev24.length > 0
        ? ev24.reduce((m, e) => (e.timestamp > m ? e.timestamp : m), ev24[0].timestamp)
        : null;
      setLastUpdated(latestTs ? new Date(latestTs.endsWith('Z') ? latestTs : latestTs + 'Z') : new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [windowHours, nodeParam, nodeParamQ, selectedEdgeNodeId]);

  useEffect(() => {
    if (!selectedEdgeNodeId && edgeNodes.length === 0) return; // wait for node list
    fetchAll();
    clearInterval(pollRef.current);
    pollRef.current = setInterval(fetchAll, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [fetchAll, selectedEdgeNodeId, edgeNodes.length]);

  // Clear selected node when switching modes
  const handleModeChange = useCallback((mode) => {
    setActiveMode(mode);
    setSelectedNode(null);
  }, []);

  const isOnline = edgeNode?.status === 'Online' || edgeNode?.status === 'Active';
  // The header names the site, never the agent's hostname: the /edge-nodes/ row carries
  // custom_tags (the display name); /topology/edge-node does not, so it is only the fallback.
  const listNode = edgeNodes.find(n => n.edge_node_id === (selectedEdgeNodeId || edgeNode?.edge_node_id));
  const headerNode = listNode || edgeNode;
  // The tab bodies get the /topology/edge-node row (policy counts, enforcement mode) plus the list row's
  // custom_tags, so they name the site the same way the header does.
  const namedEdgeNode = edgeNode ? { ...edgeNode, custom_tags: listNode?.custom_tags || {} } : null;

  return (
    <div className="etl-root flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50"
      data-etl-theme={theme} data-testid="etl-root"
      style={{ height: '100vh' }}>

      {/* Header bar */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-white/70 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          {/* Back arrow */}
          <a
            href="/dashboard"
            className="flex items-center justify-center w-7 h-7 rounded-lg bg-slate-100/70 border border-slate-200 text-slate-600 hover:text-slate-900 hover:border-cyan-400 transition-colors flex-shrink-0"
            title="Back to main"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </a>
          <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`} />
          <span className="text-sm font-semibold text-slate-800">Event Trace Light</span>
          {headerNode && (
            <span className="text-[11px] text-slate-600 hidden md:block" data-testid="etl-site-name">
              {edgeNodeDisplayName(headerNode)}
            </span>
          )}
        </div>

        {/* Mode switcher */}
        <div className="flex items-center gap-1 bg-slate-100/60 rounded-lg p-0.5 border border-slate-200">
          {MODES.map(mode => (
            <button
              key={mode.id}
              onClick={() => handleModeChange(mode.id)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-150 ${
                activeMode === mode.id
                  ? 'bg-cyan-100 text-cyan-700 border border-cyan-400'
                  : 'text-slate-600 hover:text-slate-700'
              }`}
            >
              {mode.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 text-[11px] text-slate-500">
          {activeMode !== 'global' && edgeNodes.length > 1 && (
            <AgentSelector
              edgeNodes={edgeNodes}
              selectedId={selectedEdgeNodeId}
              onSelect={setSelectedEdgeNodeId}
              theme={theme}
            />
          )}
          {error && <span className="text-red-600">{error}</span>}
          {lastUpdated && !error && (
            <span className="hidden md:block">
              Last event {fmtUtc(lastUpdated.toISOString())}
            </span>
          )}
          <ThemeToggle isDark={theme === 'dark'} onToggle={toggleTheme} />
          <button
            onClick={fetchAll}
            className="text-slate-600 hover:text-slate-700 transition-colors"
            title="Refresh now"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mode content */}
      <div className="flex-1 min-h-0">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-xs text-slate-600">Loading topology data…</span>
            </div>
          </div>
        ) : (
          <>
            {activeMode === 'overview' && (
              <TopologyOverview assets={assets} events={events} edgeNode={namedEdgeNode} windowHours={windowHours} eventTotal={eventTotal} />
            )}
            {activeMode === 'assets' && (
              <TopologyAssets
                assets={assets}
                edges={edges}
                events={events}
                edgeNode={namedEdgeNode}
                selectedNode={selectedNode}
                onSelectNode={setSelectedNode}
              />
            )}
            {activeMode === 'protocols' && (
              <TopologyProtocols assets={assets} edges={edges} events={events} events24h={events24h} edgeNode={namedEdgeNode} windowHours={windowHours} eventTotal={eventTotal} />
            )}
            {activeMode === 'attack-chain' && (
              <TopologyAttackChain
                events={events}
                assets={assets}
                edgeNode={namedEdgeNode}
                windowHours={windowHours}
                eventTotal={eventTotal}
                setWindowHours={setWindowHours}
                onRefresh={fetchAll}
              />
            )}
            {activeMode === 'global' && (
              <TopologyGlobal edgeNodes={edgeNodes} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
